// src/vdr/intralinks.ts
import * as XLSX from "xlsx";
import type { IndexRow } from "./providers"; // adjust if your types live elsewhere

// Accepts codes like "1", "1.1", "1.1.2" (pure digits + dots)
const CODE_RE = /^\d+(?:\.\d+)*$/;

// --- CSV parsing (positional, first 3 columns only) ---
function parseCsv(text: string): IndexRow[] {
  const lines = text.split(/\r?\n/).filter(l => l.trim().length);
  if (!lines.length) return [];

  const rows: IndexRow[] = [];

  // We purposely ignore headers for Intralinks; treat ALL lines as data rows.
  for (let i = 0; i < lines.length; i++) {
    const cols = safeSplit(lines[i]).map(c => c.trim());

    // We only care about the first three columns:
    // [0] Type (must be "Folder"), [1] Code, [2] Title
    const typeCell = (cols[0] ?? "").toString().trim();
    const codeRaw  = (cols[1] ?? "").toString().trim();
    const title    = (cols[2] ?? "").toString().trim();

    if (!isFolder(typeCell)) continue;
    if (!codeRaw || !CODE_RE.test(codeRaw)) continue;

    const code = normalizeTopLevelDotZero(codeRaw);
    rows.push({ code, title });
  }

  return dedupeByCodePreferTitled(rows);
}

// --- XLS/XLSX parsing (positional, first 3 columns only) ---
async function parseXlsx(file: File): Promise<IndexRow[]> {
  const buf = await file.arrayBuffer();
  const wb = XLSX.read(buf, { type: "array" });
  const ws = wb.Sheets[wb.SheetNames[0]];
  // header:1 gives us a 2D array of rows, preserving raw positions
  const table: any[][] = XLSX.utils.sheet_to_json(ws, { header: 1, defval: "" });

  const rows: IndexRow[] = [];
  for (let i = 0; i < table.length; i++) {
    const row = table[i] ?? [];
    const typeCell = String(row[0] ?? "").trim();
    const codeRaw  = String(row[1] ?? "").trim();
    const title    = String(row[2] ?? "").trim();

    if (!isFolder(typeCell)) continue;
    if (!codeRaw || !CODE_RE.test(codeRaw)) continue;

    const code = normalizeTopLevelDotZero(codeRaw);
    rows.push({ code, title });
  }

  return dedupeByCodePreferTitled(rows);
}

/**
 * Parse an Intralinks index file (CSV/XLS/XLSX) into normalized rows.
 * - Uses only the first 3 columns: [Type, Code, Title]
 * - Keeps rows where Type == "Folder"
 * - Maps top-level codes like "1.0" -> "1" (root-level .0 compaction)
 */
export async function parseIndexFile(file: File): Promise<IndexRow[]> {
  const ext = file.name.toLowerCase().split(".").pop();
  console.log(`🧾 [intralinks] parseIndexFile: name="${file.name}" ext="${ext}" size=${(file as any)?.size ?? "?"}`);

  let rows: IndexRow[] = [];
  if (ext === "csv") {
    const text = await file.text();
    rows = parseCsv(text);
  } else if (ext === "xlsx" || ext === "xls") {
    rows = await parseXlsx(file);
  } else {
    // Fallback: treat as CSV text
    const text = await file.text();
    rows = parseCsv(text);
  }

  console.log(`   ↳ parsed rows: ${rows.length}`);
  if (rows.length > 0) {
    const sample = rows.slice(0, Math.min(3, rows.length));
    console.log("   ↳ sample:", sample);
  } else {
    console.warn("   ⚠️ No valid Intralinks rows parsed. Expect first three columns = [Folder, Code, Title].");
  }
  return rows;
}

// ---- helpers ----
function isFolder(v: string): boolean {
  return v.toLowerCase() === "folder";
}

/**
 * For Intralinks: if code is exactly one dot and ends with ".0" (e.g., "1.0", "12.0"),
 * treat it as top-level "1", "12".
 * Leaves deeper codes like "1.0.1" unchanged.
 */
function normalizeTopLevelDotZero(code: string): string {
  const dotCount = (code.match(/\./g) || []).length;
  if (dotCount === 1 && /\.0$/.test(code)) {
    return code.replace(/\.0$/, "");
  }
  return code;
}

/**
 * NEW (for Option B):
 * Given a *normalized* code, return possible root folder names in SharePoint.
 *
 * Example:
 *  code = "1"   -> ["1", "1.0"]   // so existing "1.0" in root will be reused
 *  code = "12"  -> ["12", "12.0"]
 *  code = "1.1" -> ["1.1"]        // not root, no aliasing
 */
export function getRootFolderNameCandidatesFromCode(code: string): string[] {
  const dotCount = (code.match(/\./g) || []).length;
  if (dotCount === 0) {
    // top-level code like "1", "2", "12"
    return [code, `${code}.0`];
  }
  // non-root codes: just use the code as-is
  return [code];
}

/**
 * Deduplicate by code, preferring entries with a non-empty title.
 */
function dedupeByCodePreferTitled(list: IndexRow[]): IndexRow[] {
  const byCode = new Map<string, IndexRow>();
  for (const r of list) {
    const existing = byCode.get(r.code);
    if (!existing) {
      byCode.set(r.code, r);
    } else if (!existing.title && r.title) {
      byCode.set(r.code, r);
    }
  }
  return Array.from(byCode.values());
}

/**
 * Simple CSV splitter for common cases.
 * If you already use a robust CSV library elsewhere, feel free to replace this.
 */
function safeSplit(line: string): string[] {
  // Handles basic quoted fields. Not RFC-perfect, but fine for typical VDR exports.
  const out: string[] = [];
  let cur = "";
  let inQuotes = false;

  for (let i = 0; i < line.length; i++) {
    const ch = line[i];

    if (ch === '"') {
      if (inQuotes && line[i + 1] === '"') {
        // Escaped quote
        cur += '"';
        i++;
      } else {
        inQuotes = !inQuotes;
      }
    } else if (ch === "," && !inQuotes) {
      out.push(cur);
      cur = "";
    } else {
      cur += ch;
    }
  }
  out.push(cur);
  return out;
}
